import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  ButtonContainer,
  Card,
  ContentSection,
  Heading,
  Header,
  Icon,
  IconList,
  Layout,
  Main,
  Paragraph,
  Spinner,
  StatusMessage,
} from '@vwfs-bronson/bronson-react';
import { BrowserRouter, NavLink, matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  AuthConfig,
  AuthTokens,
  clearAuthSession,
  exchangeCodeForTokens,
  getEmailFromIdToken,
  getGroupsFromAccessToken,
  getStoredTokens,
  redirectToLogin,
  redirectToLogout,
} from './auth';
import {
  Stage,
  allStages,
  entitledStages,
  pickInitialStage,
  writeLastStage,
} from './environments';
import {
  Agent,
  AgentListResponse,
  EnvironmentListResponse,
  FesConfig,
  JobFilters,
  JobListResponse,
  ModelDefinition,
  ModelListResponse,
  Skill,
  SkillListResponse,
  Team,
  TeamListResponse,
  UnclaimedInstallationListResponse,
  getAgents,
  getEnvironments,
  getJobs,
  getModels,
  getSkills,
  getTeams,
  getUnclaimedInstallations,
  isForbidden,
  syncAuthenticatedUser,
} from './fes';
import {
  ConfigurationView,
  canEditConfigurationView,
  canViewConfigurationView,
  getConfigurationViewFromPath,
  getConfigurationViewLabel,
  getConfigurationViewPath,
  getDefaultConfigurationView,
  getGitHubAppUrl,
  getNavGroups,
} from './views';
import { Session, hasNoTeam, toSession } from './session';
import { EnvironmentsTable } from './components/EnvironmentsTable';
import { GitHubAppsTable } from './components/GitHubAppsTable';
import { TableLoading } from './components/TableLoading';
import { AgentsView } from './components/agents/AgentsView';
import { BackofficeChatView } from './components/chat/BackofficeChatView';
import { JobsView } from './components/jobs/JobsView';
import { SchedulesView } from './components/schedules/SchedulesView';
import { UsageReportView } from './components/usage/UsageReportView';
import { SkillsView } from './components/skills/SkillsView';
import { TeamsView } from './components/teams/TeamsView';
import { ModelsView } from './components/models/ModelsView';
import { HomeView } from './components/HomeView';
import { ReactComponent as AgentMark } from './assets/agent-mark.svg';
import { ReactComponent as GitHubMark } from './assets/github-mark.svg';
import { ReactComponent as LogoutIcon } from './assets/logout.svg';
import './App.css';

type ViewState =
  | { status: 'loading'; message: string }
  | { status: 'signedOut' }
  | { status: 'callback'; message: string }
  | {
      status: 'ready';
      tokens: AuthTokens;
      session: Session;
      activeView: ConfigurationView;
      viewErrors?: Partial<Record<ConfigurationView, string>>;
      teams?: TeamListResponse;
      agents?: AgentListResponse;
      skills?: SkillListResponse;
      environments?: EnvironmentListResponse;
      unclaimedInstallations?: UnclaimedInstallationListResponse;
      jobs?: JobListResponse;
      models?: ModelListResponse;
      loadingView?: ConfigurationView;
    }
  | { status: 'error'; message: string };

type ReadyViewData = Partial<
  Omit<Extract<ViewState, { status: 'ready' }>, 'activeView' | 'status' | 'tokens' | 'session' | 'viewErrors'>
>;

const authReturnPathKey = 'agent_backoffice_auth_return_path';
const themeStorageKey = 'agent_backoffice_theme';
const documentationUrl = 'https://docs.dev.ai-agent.vwfs.io/index.html';

type Theme = 'light' | 'dark';

const navItemIcons: Record<ConfigurationView, string> = {
  Home: 'semantic-grid',
  Models: 'semantic-list',
  Agents: 'semantic-user',
  Skills: 'semantic-settings',
  Teams: 'semantic-briefcase',
  'GitHub Apps': 'semantic-share',
  Environments: 'semantic-save-cloud',
  Jobs: 'semantic-performance',
  Schedules: 'semantic-calendar',
  Usage: 'semantic-calculator',
  Chat: 'semantic-chat',
};

function getInitialTheme(): Theme {
  const storedTheme = window.localStorage.getItem(themeStorageKey);

  if (storedTheme === 'light' || storedTheme === 'dark') {
    return storedTheme;
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function ThemeButton({ theme, onToggle }: { theme: Theme; onToggle: () => void }) {
  const isDark = theme === 'dark';
  const nextTheme = isDark ? 'light' : 'dark';

  return (
    <Button
      ariaLabel={`Switch to ${nextTheme} mode`}
      className="app-theme-button"
      link
      onClick={onToggle}
      simple
      title={`Switch to ${nextTheme} mode`}
    >
      <span className="app-theme-icon" aria-hidden="true">{isDark ? '☀' : '☾'}</span>
    </Button>
  );
}

let callbackExchangePromise: Promise<AuthTokens> | null = null;

function getViewLoadingMessage(view: ConfigurationView): string {
  return `Loading ${getConfigurationViewLabel(view).toLowerCase()} from FES...`;
}

function hasViewData(view: Extract<ViewState, { status: 'ready' }>, item: ConfigurationView): boolean {
  if (view.viewErrors?.[item]) {
    return true;
  }

  if (item === 'Home' || item === 'Chat' || item === 'Usage' || item === 'Schedules') {
    return true;
  }

  if (item === 'Teams') {
    return Boolean(view.teams);
  }

  if (item === 'Models') {
    return Boolean(view.models);
  }

  if (item === 'Agents') {
    return Boolean(view.agents);
  }

  if (item === 'Skills') {
    return Boolean(view.skills);
  }

  if (item === 'Environments') {
    return Boolean(view.environments);
  }

  if (item === 'GitHub Apps') {
    return Boolean(view.unclaimedInstallations);
  }

  return Boolean(view.jobs);
}

function getPathFromReturnPath(returnPath: string): string {
  try {
    return new URL(returnPath, window.location.origin).pathname;
  } catch {
    return '/';
  }
}

function getSafeReturnPath(pathname: string, search: string): string {
  if (pathname === '/auth/callback') {
    return '/';
  }

  return `${pathname}${search}`;
}

function decodeRouteParam(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeRouteView = useMemo(() => getConfigurationViewFromPath(location.pathname), [location.pathname]);
  const agentEditMatch = matchPath('/agents/:agentId/edit', location.pathname);
  const skillEditMatch = matchPath('/skills/:skillId/edit', location.pathname);
  const isCreatingAgent = Boolean(matchPath('/agents/new', location.pathname));
  const isCreatingSkill = Boolean(matchPath('/skills/new', location.pathname));
  const isViewingTeamDetail = Boolean(matchPath('/teams/:teamId', location.pathname));
  const editingAgentId = decodeRouteParam(agentEditMatch?.params.agentId);
  const editingSkillId = decodeRouteParam(skillEditMatch?.params.skillId);
  const authConfig = useMemo<AuthConfig>(
    () => ({
      cognitoDomain: process.env.REACT_APP_COGNITO_DOMAIN || '',
      clientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
      redirectUri: process.env.REACT_APP_COGNITO_REDIRECT_URI || '',
      logoutUri: process.env.REACT_APP_COGNITO_LOGOUT_URI || '',
      scopes: ['openid', 'email', 'profile'],
    }),
    []
  );
  // The stage this session is pointed at. Every FES call flows through fesConfig, so changing
  // it here redirects the whole app -- including the chat event stream -- at the next stage.
  const [stage, setStage] = useState<Stage>(() =>
    pickInitialStage(entitledStages(getGroupsFromAccessToken(getStoredTokens()?.accessToken)))
  );
  const fesConfig = useMemo<FesConfig>(() => ({ baseUrl: stage.fesBaseUrl }), [stage]);
  const githubAppUrl = useMemo(() => getGitHubAppUrl(stage.name), [stage]);
  const [view, setView] = useState<ViewState>({ status: 'loading', message: 'Checking authentication session...' });
  // Only the stages this token holds a group for. FES answers 403 to any other, so offering
  // one would just be a dead entry in the switcher.
  const accessToken = view.status === 'ready' ? view.tokens.accessToken : undefined;
  const selectableStages = useMemo(() => entitledStages(getGroupsFromAccessToken(accessToken)), [accessToken]);
  const stageRef = useRef(stage.name);
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const hasInitialized = useRef(false);
  const jobsRequestId = useRef(0);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    window.localStorage.setItem(themeStorageKey, theme);
  }, [theme]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [mobileNavOpen]);

  const loadViewData = useCallback(
    async (item: ConfigurationView, tokens: AuthTokens): Promise<ReadyViewData> => {
      if (item === 'Teams') {
        return { teams: await getTeams(fesConfig, tokens) };
      }

      if (item === 'Models') {
        return { models: await getModels(fesConfig, tokens) };
      }

      if (item === 'Agents') {
        return { agents: await getAgents(fesConfig, tokens) };
      }

      if (item === 'Skills') {
        return { skills: await getSkills(fesConfig, tokens) };
      }

      if (item === 'Environments') {
        return { environments: await getEnvironments(fesConfig, tokens) };
      }

      if (item === 'GitHub Apps') {
        return { unclaimedInstallations: await getUnclaimedInstallations(fesConfig, tokens) };
      }

      if (item === 'Jobs') {
        return { jobs: await getJobs(fesConfig, tokens) };
      }

      if (item === 'Chat') {
        return {};
      }

      if (item === 'Home') {
        return {};
      }

      if (item === 'Schedules') {
        return {};
      }

      if (item === 'Usage') {
        return {};
      }

      return {};
    },
    [fesConfig]
  );

  const loadInitialView = useCallback(
    async (tokens: AuthTokens, pathname: string) => {
      setView({ status: 'loading', message: 'Signing in...' });
      const syncedUser = await syncAuthenticatedUser(fesConfig, tokens);
      const email = syncedUser.email || getEmailFromIdToken(tokens.idToken);
      const session = toSession({ ...syncedUser, ...(email && { email }) });

      const requested = getConfigurationViewFromPath(pathname);
      const target =
        canViewConfigurationView(session, requested)
          ? requested
          : getDefaultConfigurationView(session);

      if (target !== requested || (target === 'Home' && pathname !== '/')) {
        navigate(getConfigurationViewPath(target), { replace: true });
      }

      setView({ status: 'loading', message: getViewLoadingMessage(target) });

      const teams = await getTeams(fesConfig, tokens).catch(error => {
        console.error('Unable to load teams.', error);
        return undefined;
      });

      try {
        const viewData = await loadViewData(target, tokens);
        setView({ status: 'ready', tokens, session, activeView: target, teams, ...viewData });
      } catch (error) {
        if (!isForbidden(error)) {
          throw error;
        }

        setView({
          status: 'ready',
          tokens,
          session,
          activeView: target,
          teams,
          viewErrors: { [target]: (error as Error).message },
        });
      }
    },
    [fesConfig, loadViewData, navigate]
  );

  const handleStageChange = useCallback(
    (name: string) => {
      const next = allStages().find(candidate => candidate.name === name);

      if (!next || next.name === stage.name) {
        return;
      }

      // Teams, agents and jobs are per stage, so any detail route in the address bar names a
      // record the next stage does not have. Drop back to the current view's list.
      navigate(getConfigurationViewPath(activeRouteView));
      writeLastStage(next);
      setStage(next);
    },
    [activeRouteView, navigate, stage.name]
  );

  // Reloads the session against the newly selected stage. Roles and teams are per stage, so
  // this re-runs /auth/sync rather than reusing the session we already hold.
  useEffect(() => {
    if (stageRef.current === stage.name) {
      return;
    }

    stageRef.current = stage.name;

    const tokens = getStoredTokens();

    if (!tokens) {
      setView({ status: 'signedOut' });
      return;
    }

    setView({ status: 'loading', message: `Switching to ${stage.label}...` });

    void (async () => {
      try {
        await loadInitialView(tokens, window.location.pathname);
      } catch (error) {
        // Deliberately does not clear the session: being refused by one stage says nothing
        // about the others, and signing the user out would lose the stage they came from.
        setView({ status: 'error', message: error instanceof Error ? error.message : 'FES request failed.' });
      }
    })();
  }, [stage.name, stage.label, loadInitialView]);

  const handleJobsPageChange = async (offset: number, filters: JobFilters = {}) => {
    if (view.status !== 'ready') {
      return;
    }

    const requestId = ++jobsRequestId.current;
    try {
      const jobs = await getJobs(fesConfig, view.tokens, { ...filters, offset });
      if (requestId === jobsRequestId.current) {
        setView(latestView => (latestView.status === 'ready' ? { ...latestView, jobs } : latestView));
      }
    } catch (error) {
      if (requestId === jobsRequestId.current) {
        setView({ status: 'error', message: error instanceof Error ? error.message : 'Unable to load jobs.' });
      }
    }
  };

  const handleTeamSaved = (savedTeam: Team) => {
    setView(latestView => {
      if (latestView.status !== 'ready' || !latestView.teams) {
        return latestView;
      }

      const currentTeams = latestView.teams.data || [];
      const savedTeamExists = currentTeams.some(team => team.teamId === savedTeam.teamId);

      return {
        ...latestView,
        teams: {
          ...latestView.teams,
          data: savedTeamExists
            ? currentTeams.map(team => (team.teamId === savedTeam.teamId ? { ...team, ...savedTeam } : team))
            : [savedTeam, ...currentTeams],
          pagination: latestView.teams.pagination
            ? {
                ...latestView.teams.pagination,
                total:
                  typeof latestView.teams.pagination.total === 'number' && !savedTeamExists
                    ? latestView.teams.pagination.total + 1
                    : latestView.teams.pagination.total,
              }
            : latestView.teams.pagination,
        },
      };
    });
  };

  const handleAgentSaved = (savedAgent: Agent) => {
    setView(latestView => {
      if (latestView.status !== 'ready' || !latestView.agents) {
        return latestView;
      }

      const currentAgents = latestView.agents.data || [];
      const savedAgentExists = currentAgents.some(agent => agent.agentId === savedAgent.agentId);

      return {
        ...latestView,
        agents: {
          ...latestView.agents,
          data: savedAgentExists
            ? currentAgents.map(agent => (agent.agentId === savedAgent.agentId ? { ...agent, ...savedAgent } : agent))
            : [savedAgent, ...currentAgents],
          pagination: latestView.agents.pagination
            ? {
                ...latestView.agents.pagination,
                total:
                  typeof latestView.agents.pagination.total === 'number' && !savedAgentExists
                    ? latestView.agents.pagination.total + 1
                    : latestView.agents.pagination.total,
              }
            : latestView.agents.pagination,
        },
      };
    });
  };

  const handleModelSaved = (savedModel: ModelDefinition) => {
    setView(latestView => {
      if (latestView.status !== 'ready' || !latestView.models) return latestView;
      const currentModels = latestView.models.data || [];
      const exists = currentModels.some(model => model.modelId === savedModel.modelId);
      return {
        ...latestView,
        models: {
          ...latestView.models,
          data: exists
            ? currentModels.map(model => model.modelId === savedModel.modelId ? savedModel : model)
            : [savedModel, ...currentModels],
        },
      };
    });
  };

  const handleAgentDeleted = (deletedAgentId: string) => {
    setView(latestView => {
      if (latestView.status !== 'ready' || !latestView.agents) {
        return latestView;
      }

      const currentAgents = latestView.agents.data || [];

      return {
        ...latestView,
        agents: {
          ...latestView.agents,
          data: currentAgents.filter(agent => agent.agentId !== deletedAgentId),
          pagination: latestView.agents.pagination
            ? {
                ...latestView.agents.pagination,
                total:
                  typeof latestView.agents.pagination.total === 'number'
                    ? Math.max(0, latestView.agents.pagination.total - 1)
                    : latestView.agents.pagination.total,
              }
            : latestView.agents.pagination,
        },
      };
    });
  };

  const handleSkillSaved = (savedSkill: Skill) => {
    setView(latestView => {
      if (latestView.status !== 'ready' || !latestView.skills) {
        return latestView;
      }

      const currentSkills = latestView.skills.data || [];
      const savedSkillExists = currentSkills.some(skill => skill.skillId === savedSkill.skillId);

      return {
        ...latestView,
        skills: {
          ...latestView.skills,
          data: savedSkillExists
            ? currentSkills.map(skill => (skill.skillId === savedSkill.skillId ? { ...skill, ...savedSkill } : skill))
            : [savedSkill, ...currentSkills],
          pagination: latestView.skills.pagination
            ? {
                ...latestView.skills.pagination,
                total:
                  typeof latestView.skills.pagination.total === 'number' && !savedSkillExists
                    ? latestView.skills.pagination.total + 1
                    : latestView.skills.pagination.total,
              }
            : latestView.skills.pagination,
        },
      };
    });
  };

  const handleSkillDeleted = (deletedSkillId: string) => {
    setView(latestView => {
      if (latestView.status !== 'ready' || !latestView.skills) {
        return latestView;
      }

      const currentSkills = latestView.skills.data || [];

      return {
        ...latestView,
        skills: {
          ...latestView.skills,
          data: currentSkills.filter(skill => skill.skillId !== deletedSkillId),
          pagination: latestView.skills.pagination
            ? {
                ...latestView.skills.pagination,
                total:
                  typeof latestView.skills.pagination.total === 'number'
                    ? Math.max(0, latestView.skills.pagination.total - 1)
                    : latestView.skills.pagination.total,
              }
            : latestView.skills.pagination,
        },
      };
    });
  };

  useEffect(() => {
    if (view.status === 'ready' && activeRouteView === 'Home' && location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [activeRouteView, location.pathname, navigate, view.status]);

  useEffect(() => {
    if (view.status !== 'ready') {
      return;
    }

    const hasActiveViewData = hasViewData(view, activeRouteView);

    if (view.activeView !== activeRouteView || (!hasActiveViewData && view.loadingView !== activeRouteView)) {
      setView(currentView =>
        currentView.status === 'ready'
          ? {
              ...currentView,
              activeView: activeRouteView,
              loadingView: hasViewData(currentView, activeRouteView) ? currentView.loadingView : activeRouteView,
            }
          : currentView
      );
    }

    if (hasActiveViewData || view.loadingView === activeRouteView) {
      return;
    }

    const load = async () => {
      try {
        const viewData = await loadViewData(activeRouteView, view.tokens);

        setView(latestView =>
          latestView.status === 'ready'
            ? {
                ...latestView,
                ...viewData,
                viewErrors: { ...latestView.viewErrors, [activeRouteView]: undefined },
                loadingView: latestView.loadingView === activeRouteView ? undefined : latestView.loadingView,
              }
            : latestView
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : `Unable to load ${activeRouteView}.`;

        if (!isForbidden(error)) {
          setView({ status: 'error', message });
          return;
        }

        setView(latestView =>
          latestView.status === 'ready'
            ? {
                ...latestView,
                viewErrors: { ...latestView.viewErrors, [activeRouteView]: message },
                loadingView: latestView.loadingView === activeRouteView ? undefined : latestView.loadingView,
              }
            : latestView
        );
      }
    };

    void load();
  }, [activeRouteView, loadViewData, view]);

  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;

    const initialize = async () => {
      const params = new URLSearchParams(window.location.search);
      const authError = params.get('error');
      const code = params.get('code');
      const state = params.get('state');

      if (authError) {
        setView({
          status: 'error',
          message: `${authError}: ${params.get('error_description') || 'Cognito returned an authentication error.'}`,
        });
        return;
      }

      if (code && state) {
        setView({ status: 'callback', message: 'Completing Cognito login...' });

        try {
          if (!callbackExchangePromise) {
            callbackExchangePromise = exchangeCodeForTokens(authConfig, code, state);
          }

          const tokens = await callbackExchangePromise;
          const returnPath = window.sessionStorage.getItem(authReturnPathKey) || '/';
          window.sessionStorage.removeItem(authReturnPathKey);

          navigate(returnPath, { replace: true });
          await loadInitialView(tokens, getPathFromReturnPath(returnPath));
        } catch (error) {
          callbackExchangePromise = null;
          clearAuthSession();
          setView({ status: 'error', message: error instanceof Error ? error.message : 'Cognito login failed.' });
        }

        return;
      }

      const tokens = getStoredTokens();

      if (!tokens) {
        setView({ status: 'signedOut' });
        return;
      }

      try {
        await loadInitialView(tokens, window.location.pathname);
      } catch (error) {
        clearAuthSession();
        setView({ status: 'error', message: error instanceof Error ? error.message : 'FES request failed.' });
      }
    };

    void initialize();
  }, [activeRouteView, authConfig, loadInitialView, navigate]);

  const handleLogin = () => {
    window.sessionStorage.setItem(authReturnPathKey, getSafeReturnPath(location.pathname, location.search));
    redirectToLogin(authConfig).catch(error => {
      setView({ status: 'error', message: error instanceof Error ? error.message : 'Unable to start Cognito login.' });
    });
  };

  const appHeader = (
    <Header
      className="app-header"
      logo={(
        <NavLink aria-label="Agent Backoffice home" className="app-header-brand" to="/">
          <AgentMark aria-hidden="true" className="app-header-mark" />
          <span>Agent Backoffice</span>
        </NavLink>
      )}
      notificationCenter={(
        <div className="app-header-actions">
          {view.status === 'ready' ? (
            <Button
              aria-controls="primary-navigation"
              ariaExpanded={mobileNavOpen ? 'true' : 'false'}
              ariaLabel={mobileNavOpen ? 'Hide navigation' : 'Show navigation'}
              className="app-mobile-nav-toggle"
              icon={mobileNavOpen ? 'semantic-close' : 'semantic-menu'}
              link
              onClick={() => setMobileNavOpen(open => !open)}
              simple
            />
          ) : null}
          {view.status === 'ready' ? (
            <div className={`app-stage${stage.isProduction ? ' app-stage--production' : ''}`}>
              <label className="app-stage-label" htmlFor="app-stage-select">
                Stage
              </label>
              {selectableStages.length > 1 ? (
                <span className="app-stage-control">
                  <select
                    className="app-stage-select"
                    id="app-stage-select"
                    onChange={event => handleStageChange(event.target.value)}
                    title="Switch the stage this backoffice is pointed at"
                    value={stage.name}
                  >
                    {selectableStages.map(option => (
                      <option key={option.name} value={option.name}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </span>
              ) : (
                <span className="app-stage-name" id="app-stage-select">
                  {stage.label}
                </span>
              )}
            </div>
          ) : null}
          {view.status === 'ready' ? (
            <div className="app-header-user">
              <span className="app-header-email" title={view.session.email || 'Email unavailable'}>
                {view.session.email || 'Email unavailable'}
              </span>
              <button
                aria-label="Log out"
                className="app-logout-button"
                title="Log out"
                onClick={() => redirectToLogout(authConfig)}
                type="button"
              >
                <LogoutIcon aria-hidden="true" className="app-logout-icon" />
              </button>
            </div>
          ) : null}
          <div className="app-header-theme">
            <ThemeButton theme={theme} onToggle={() => setTheme(current => (current === 'light' ? 'dark' : 'light'))} />
          </div>
        </div>
      )}
      staticHeader
    />
  );

  if (view.status === 'ready') {
    const teams = view.teams?.data || [];
    const totalTeams = view.teams?.pagination?.total ?? teams.length;
    const agents = view.agents?.data || [];
    const totalAgents = view.agents?.pagination?.total ?? agents.length;
    const skills = view.skills?.data || [];
    const totalSkills = view.skills?.pagination?.total ?? skills.length;
    const environments = view.environments?.data || [];
    const totalEnvironments = environments.length;
    const unclaimedInstallations = view.unclaimedInstallations?.data || [];
    const totalUnclaimedInstallations = view.unclaimedInstallations?.pagination?.total ?? unclaimedInstallations.length;
    const jobs = view.jobs?.data || [];
    const models = view.models?.data || [];
    const totalJobs = view.jobs?.pagination?.total ?? jobs.length;
    const isLoadingActiveView = view.loadingView === view.activeView;
    const activeTotalLabels: Partial<Record<ConfigurationView, string>> = {
      Agents: `${totalAgents} ${totalAgents === 1 ? 'agent' : 'agents'}`,
      Environments: `${totalEnvironments} ${totalEnvironments === 1 ? 'environment' : 'environments'}`,
      'GitHub Apps': `${totalUnclaimedInstallations} ${
        totalUnclaimedInstallations === 1 ? 'unassigned installation' : 'unassigned installations'
      }`,
      Skills: `${totalSkills} ${totalSkills === 1 ? 'skill' : 'skills'}`,
      Teams: `${totalTeams} ${totalTeams === 1 ? 'team' : 'teams'}`,
      Jobs: `${totalJobs} ${totalJobs === 1 ? 'job' : 'jobs'}`,
      Models: `${models.length} ${models.length === 1 ? 'model' : 'models'}`,
      Schedules: 'Recurring agent runs',
      Usage: 'Long-term billing analytics',
      Chat: 'Conversations',
    };
    const { session } = view;
    const activeViewError = view.viewErrors?.[view.activeView];
    const canEditActiveView = canEditConfigurationView(session, view.activeView);
    const visibleNavGroups = getNavGroups(session);
    const showNoTeamNotice =
      view.activeView !== 'Home' &&
      hasNoTeam(session) &&
      (view.activeView !== 'Agents' || agents.length === 0);

    return (
      <>
        {appHeader}
        <main className={view.activeView === 'Chat' ? 'app-dashboard app-dashboard--chat' : 'app-dashboard'}>
        {mobileNavOpen ? (
          <div className="app-nav-backdrop" aria-hidden="true" onClick={() => setMobileNavOpen(false)} />
        ) : null}
        <aside
          className={mobileNavOpen ? 'app-sidebar app-sidebar--open' : 'app-sidebar'}
          id="primary-navigation"
          aria-label="Primary navigation"
        >
          <nav className="app-nav" aria-label="Configuration navigation">
            {visibleNavGroups.map(group => (
              <div key={group.label}>
                <div className="app-nav-group">{group.label}</div>
                <ul className="app-nav-list">
                  {group.items.map(item => (
                    <li key={item}>
                      <NavLink
                        className={({ isActive }) => (isActive ? 'app-nav-link app-nav-link--active' : 'app-nav-link')}
                        onClick={() => setMobileNavOpen(false)}
                        to={getConfigurationViewPath(item)}
                      >
                        <Icon name={navItemIcons[item]} size="xsmall" />
                      <span>{getConfigurationViewLabel(item)}</span>
                      </NavLink>
                    </li>
                  ))}
                  {group.label === 'Access' ? (
                    <li>
                      <a
                        className="app-nav-link"
                        href={githubAppUrl}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <GitHubMark aria-hidden="true" className="app-nav-svg-icon" />
                        <span>GitHub App</span>
                      </a>
                    </li>
                  ) : null}
                </ul>
              </div>
            ))}
            <div>
              <div className="app-nav-group">Help</div>
              <ul className="app-nav-list">
                <li>
                  <a
                    className="app-nav-link"
                    href={documentationUrl}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Icon name="semantic-brochure" size="xsmall" />
                    <span>Documentation</span>
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </aside>
        <section
          className={view.activeView === 'Chat' ? 'app-content app-content--chat' : 'app-content'}
          aria-labelledby="configuration-heading"
        >
          {view.activeView === 'Chat' ? (
            <Heading level={1} id="configuration-heading" className="u-visually-hidden">
              Chat
            </Heading>
          ) : (
            <header
              className={view.activeView === 'Home' ? 'app-content-header app-content-header--home' : 'app-content-header'}
            >
              <div>
                <Heading level={1} id="configuration-heading" className="app-title">
                  {view.activeView === 'Home' ? 'Overview' : getConfigurationViewLabel(view.activeView)}
                </Heading>
                {view.activeView !== 'Home' && !isViewingTeamDetail ? (
                  <p className="app-summary">{isLoadingActiveView ? 'Loading...' : activeTotalLabels[view.activeView]}</p>
                ) : null}
              </div>
            </header>
          )}
          {showNoTeamNotice ? (
            <div className="app-result" role="status">
              You are not a member of any team yet, so there is nothing to show here. Ask an
              administrator to add you to one — team membership is assigned in the backoffice, not in
              Cognito.
            </div>
          ) : null}
          {activeViewError ? (
            <div className="app-result" role="alert">
              {activeViewError}
            </div>
          ) : null}
          {isLoadingActiveView ? <TableLoading view={view.activeView} /> : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Home' ? (
            <HomeView onNavigate={path => navigate(path)} />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Models' ? (
            <ModelsView
              canEdit={canEditActiveView}
              fesConfig={fesConfig}
              models={models}
              onModelSaved={handleModelSaved}
              tokens={view.tokens}
            />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Skills' ? (
            <SkillsView
              creatingSkill={isCreatingSkill}
              editingSkillId={editingSkillId}
              fesConfig={fesConfig}
              tokens={view.tokens}
              session={session}
              skills={skills}
              teams={teams}
              onEditSkillClose={() => navigate(getConfigurationViewPath('Skills'))}
              onEditSkillOpen={skill => navigate(`/skills/${encodeURIComponent(skill.skillId)}/edit`)}
              onCreateSkillClose={() => navigate(getConfigurationViewPath('Skills'))}
              onCreateSkillOpen={() => navigate('/skills/new')}
              onSkillSaved={handleSkillSaved}
              onSkillDeleted={handleSkillDeleted}
            />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Teams' ? (
            <TeamsView
              fesConfig={fesConfig}
              tokens={view.tokens}
              teams={teams}
              session={session}
              canEdit={canEditActiveView}
              onTeamSaved={handleTeamSaved}
            />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Agents' ? (
            <AgentsView
              creatingAgent={isCreatingAgent}
              editingAgentId={editingAgentId}
              fesConfig={fesConfig}
              tokens={view.tokens}
              agents={agents}
              session={session}
              teams={teams}
              onAgentDeleted={handleAgentDeleted}
              onAgentSaved={handleAgentSaved}
              onCreateAgentClose={() => navigate(getConfigurationViewPath('Agents'))}
              onCreateAgentOpen={() => navigate('/agents/new')}
              onEditAgentClose={() => navigate(getConfigurationViewPath('Agents'))}
              onEditAgentOpen={agent => navigate(`/agents/${encodeURIComponent(agent.agentId)}/edit`)}
            />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'GitHub Apps' ? (
            <GitHubAppsTable installations={unclaimedInstallations} />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Environments' ? <EnvironmentsTable environments={environments} /> : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Jobs' ? (
            <JobsView
              fesConfig={fesConfig}
              tokens={view.tokens}
              jobs={jobs}
              pagination={view.jobs?.pagination}
              onPageChange={handleJobsPageChange}
            />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Schedules' ? (
            <SchedulesView fesConfig={fesConfig} tokens={view.tokens} />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Usage' ? (
            <UsageReportView fesConfig={fesConfig} tokens={view.tokens} />
          ) : null}
          {!isLoadingActiveView && !activeViewError && view.activeView === 'Chat' ? <BackofficeChatView fesConfig={fesConfig} tokens={view.tokens} /> : null}
        </section>
        </main>
      </>
    );
  }

  if (view.status === 'loading' || view.status === 'callback') {
    return (
      <>
        {appHeader}
        <Main>
          <div className="app-auth-loading" role="status">
            <Spinner busy center small>{view.message}</Spinner>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      {appHeader}
      <Main>
        <ContentSection className="app-login-shell" pageWrap pageWrapSize="small">
          <Card className="app-login-card" element="article">
            <Layout divider equalHeight middle>
              <Layout.Item default="1/2" s="1/1">
                <div className="app-login-introduction u-text-left">
                  <Heading level={1} id="home-heading" className="app-title">
                    Manage your agent workspace
                  </Heading>
                  <Paragraph className="u-text-left">
                    A secure administration workspace for governing the complete agent lifecycle.
                  </Paragraph>
                  <IconList className="app-login-capabilities">
                    <IconList.Item icon="semantic-checkmark" iconMeaning="Included" success>
                      Standardize reusable agent and skill definitions
                    </IconList.Item>
                    <IconList.Item icon="semantic-checkmark" iconMeaning="Included" success>
                      Control team ownership and environment access
                    </IconList.Item>
                    <IconList.Item icon="semantic-checkmark" iconMeaning="Included" success>
                      Review execution history and platform usage
                    </IconList.Item>
                  </IconList>
                </div>
              </Layout.Item>
              <Layout.Item default="1/2" s="1/1">
                <div className="app-login-auth u-text-center">
                  <Heading level={2} className="app-title">Welcome back</Heading>
                  <Paragraph>Sign in with Cognito using your company credentials to continue.</Paragraph>
                  {view.status === 'error' ? (
                    <div className="app-login-status" role="alert">
                      <StatusMessage status="error" label={view.message} />
                    </div>
                  ) : null}
                  {view.status === 'signedOut' || view.status === 'error' ? (
                    <ButtonContainer className="app-login-actions" center>
                      <Button onClick={handleLogin}>
                        Sign in
                      </Button>
                    </ButtonContainer>
                  ) : null}
                </div>
              </Layout.Item>
            </Layout>
          </Card>
        </ContentSection>
      </Main>
    </>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
